import { Dispatch, SetStateAction, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIVE_TAB_PARAM } from '../../constants';
import { MeetingState, Orientation, PracticeProspect, SimulationsTab } from '../../types';
import { Divider, ProspectDetails, TagsAndNotesCell } from '../shared';
import DialerEndActions, { NextProspectDetails } from './DialerEndActions';
import DialerStatus from './DialerStatus';

interface DialerContentProps {
  callSid: string;
  currProspect: PracticeProspect;
  isDaily: boolean;
  isMuted: boolean;
  meetingState: MeetingState;
  callNextProspect: () => void;
  closeDialer: () => void;
  redial: () => void;
  setProspects: Dispatch<SetStateAction<PracticeProspect[]>>;
  stopCall: () => void;
  toggleMute: () => void;
  isRedialing?: boolean;
  nextProspectDetails?: NextProspectDetails;
}

const DialerContent = ({
  callSid,
  currProspect,
  isDaily,
  isMuted,
  meetingState,
  callNextProspect,
  closeDialer,
  redial,
  setProspects,
  stopCall,
  toggleMute,
  isRedialing,
  nextProspectDetails,
}: DialerContentProps) => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get(ACTIVE_TAB_PARAM) as SimulationsTab;

  const isInFavorites = activeTab === SimulationsTab.FAVORITES;

  const { personaId, accountName, firstName, lastName, isFavorited, jobTitle, tags, notes, isDraft } = currProspect;

  const onFavoriteSuccess = useCallback(
    (newIsFavorited: boolean) => {
      if (!newIsFavorited && isInFavorites) {
        // If we are removing a prospect from favorites from within the favorites page,
        // remove it from the list of prospects.
        setProspects((prev) => prev.filter((p) => p.personaId !== personaId));
      } else {
        // Otherwise, just update the prospect's favorite status.
        setProspects((prev) =>
          prev.map((p) => (p.personaId === personaId ? { ...p, isFavorited: newIsFavorited } : p))
        );
      }
    },
    [currProspect, isInFavorites, personaId, setProspects]
  );
  const prospectDetailsSharedProps = {
    accountName,
    firstName,
    lastName,
    jobTitle,
  };

  return (
    <div className="flex items-center gap-8">
      <div className="flex-1">
        {/* Drafts can't be favorited. */}
        {isDraft && <ProspectDetails {...prospectDetailsSharedProps} />}
        {!isDraft && (
          <ProspectDetails
            {...prospectDetailsSharedProps}
            isFavorited={isFavorited}
            onFavoriteSuccess={onFavoriteSuccess}
            personaId={personaId}
          />
        )}
      </div>
      <Divider type={Orientation.VERTICAL} />
      <div className="flex-1">
        <TagsAndNotesCell prospectId={personaId} prospectTags={tags} notes={notes} />
      </div>
      <Divider type={Orientation.VERTICAL} />
      <div className="flex-1">
        <DialerStatus
          callSid={callSid}
          currProspect={currProspect}
          isDaily={isDaily}
          isRedialing={isRedialing}
          meetingState={meetingState}
          handleRedial={redial}
        />
      </div>
      <Divider type={Orientation.VERTICAL} />
      <div className="flex-1">
        <DialerEndActions
          isMuted={isMuted}
          meetingState={meetingState}
          nextProspectDetails={nextProspectDetails}
          callNextProspect={callNextProspect}
          exitCall={closeDialer}
          stopCall={stopCall}
          toggleMute={toggleMute}
        />
      </div>
    </div>
  );
};

export default DialerContent;
